import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { AbstractApiService } from '@app/core/http/abstract-api.service';
import { SanitizerService }   from '@app/core/http/sanitizer.service';
import { TransitionService }  from '@app/core/http/transition.service';
import { ModelTypeEnum }      from '@app/core/models/transition.model';
import { Tenant }             from '@app/modules/admin-tenant/models/tenant.model';
import {
	ElsterConfig,
	NotificationConfig,
	SchedulingConfig,
	SystemConfig
}                             from '@app/modules/configuration/models/configuration.model';
import { Address }            from '@app/shared/components/address/models/address.model';
import { LoggerService }      from '@app/shared/services/logger.service';
import {
	RequestActionEnum,
	RequestCompleteEntityState
}                             from '@core/interfaces/api.interface';
import { ConfigTypeDTO }      from '@openApiGenerated/admin-api';
import {
	ClsNotificationConfigurationDTO,
	ClsSchedulingConfigurationDTO,
	CreateTenantNotificationConfigRequestParams,
	CreateTenantSchedulingConfigRequestParams,
	CreateTenantSystemConfigRequestParams,
	ElsterConfigurationDTO,
	NotificationConfigurationDTO,
	SchedulingConfigurationDTO,
	SystemConfigurationBodyDTO,
	SystemConfigurationDTO,
	TenantDTO,
	TenantsApiClient,
	UpdateTenantNotificationConfigRequestParams,
	UpdateTenantSchedulingConfigRequestParams,
	UpdateTenantSystemConfigRequestParams
}                             from '@openApiGenerated/management-api';
import { Observable }         from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class TenantApiService extends AbstractApiService {

	constructor(
		private readonly _sanitize: SanitizerService,
		private readonly _tenantApiClient: TenantsApiClient,
		private readonly _transition: TransitionService,
		protected http: HttpClient,
		protected logger: LoggerService
	) {
		super();
		this.http = http;
	}


	// SANITIZER ######################################
	private sanitizeApiResponse( entity: Tenant, context = this ): void {
		if ( entity instanceof Tenant ) {
			entity.billingAddress = context._sanitize.address( entity.billingAddress, ModelTypeEnum.TMO ) as Address;
			entity.createdDate = context._sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.lastModifiedDate = context._sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );
		}
	}


	private sanitizeSchedulingConfigApiResponse( entity: SchedulingConfig, context = this ): void {
		context._sanitize.schedulingConfig( entity );
	}


	private sanitizeSystemConfigApiResponse( entity: SystemConfig, context = this ): void {
		context._sanitize.systemConfig( entity );
	}


	// LIST TYPE REQUESTS #############################


	// ENTITY TYPE REQUESTS ###########################
	public currentUsersTenant(): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-currentUsersTenant call' );
		const req$ = this._tenantApiClient.getTenantLoggedUser();
		return this._transition.handleEntityRequestResponse<TenantDTO, Tenant>(
			req$,
			RequestActionEnum.READ,
			Tenant,
			this,
			this.sanitizeApiResponse
		);
	}

	// Elster Config
	public readElsterConfig(
		options?: {
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ElsterConfig>> {
		const {
			withFullResponse = true
		} = options ?? {};

		this.logger.debug( 'pre-readElsterConfig call:' );

		const req$ = this._tenantApiClient.getTenantElsterConfig( withFullResponse ? 'response' : undefined );


		return this._transition.handleEntityRequestResponse<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>, ElsterConfig>(
			req$,
			RequestActionEnum.READ,
			ElsterConfig,
			this,
			this._sanitize.elsterConfig
		);
	}

	// Scheduling Config
	public readSchedulingConfig( withFullResponse = false ): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		this.logger.debug( 'pre-readTenantConfig call: ' );
		const req$ = this._tenantApiClient.getTenantSchedulingConfig( withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<SchedulingConfigurationDTO | HttpResponse<SchedulingConfigurationDTO>, SchedulingConfig>(
			req$,
			RequestActionEnum.READ,
			SchedulingConfig,
			this,
			this.sanitizeSchedulingConfigApiResponse
		);
	}

	public createSchedulingConfig( configuration: SchedulingConfig ): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		this.logger.debug( 'pre-createTenantConfig call: ', configuration );
		const schedulingConfigurationDTO = this._transition.prepareEntityRequest<SchedulingConfig, SchedulingConfigurationDTO>(
			configuration, ClsSchedulingConfigurationDTO, this
		);
		const requestParam: CreateTenantSchedulingConfigRequestParams = {
			schedulingConfigurationDTO
		};

		const req$ = this._tenantApiClient.createTenantSchedulingConfig( requestParam );
		return this._transition.handleEntityRequestResponse<SchedulingConfigurationDTO | HttpResponse<SchedulingConfigurationDTO>, SchedulingConfig>(
			req$,
			RequestActionEnum.CREATE,
			SchedulingConfig,
			this,
			this.sanitizeSchedulingConfigApiResponse
		);
	}

	public updateSchedulingConfig( configuration: SchedulingConfig ): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		this.logger.debug( 'pre-updateTenantConfig call: s', configuration );

		const schedulingConfigurationDTO = this._transition.prepareEntityRequest<SchedulingConfig, SchedulingConfigurationDTO>(
			configuration, ClsSchedulingConfigurationDTO, this
		);

		const requestParam: UpdateTenantSchedulingConfigRequestParams = {
			ifMatch: configuration?.etag ?? '-1',
			schedulingConfigurationDTO
		};

		const req$ = this._tenantApiClient.updateTenantSchedulingConfig( requestParam, 'response' );
		return this._transition.handleEntityRequestResponse<SchedulingConfigurationDTO | HttpResponse<SchedulingConfigurationDTO>, SchedulingConfig>(
			req$,
			RequestActionEnum.UPDATE,
			SchedulingConfig,
			this,
			this.sanitizeSchedulingConfigApiResponse
		);
	}


	// System Config
	public readSystemConfig(
		options?: {
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<SystemConfig>> {
		const {
			withFullResponse = true
		} = options ?? {};

		this.logger.debug( 'pre-readTenantConfig call:' );
		const req$ = this._tenantApiClient.getTenantSystemConfig( withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<SystemConfigurationDTO | HttpResponse<SystemConfigurationDTO>, SystemConfig>(
			req$,
			RequestActionEnum.READ,
			SystemConfig,
			this,
			this.sanitizeSystemConfigApiResponse
		);
	}

	public createSystemConfig(
		options: {
			configuration: SystemConfig,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<SystemConfig>> {
		const {
			configuration,
			withFullResponse = true
		} = options;

		this.logger.debug( 'pre-createTenantConfig call: ', configuration );

		const systemConfigurationBodyDTO: SystemConfigurationBodyDTO = {
			type: configuration?.type as unknown as ConfigTypeDTO,
			archiving: {
				enabled: configuration?.archiving?.enabled,
				ersCheck: configuration?.archiving?.ersCheck,
				strictReferenceCheck: configuration?.archiving?.strictReferenceCheck
			}
		};

		const requestParam: CreateTenantSystemConfigRequestParams = {
			systemConfigurationBodyDTO
		};

		const req$ = this._tenantApiClient.createTenantSystemConfig( requestParam, withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<SystemConfigurationDTO | HttpResponse<SystemConfigurationDTO>, SystemConfig>(
			req$,
			RequestActionEnum.CREATE,
			SystemConfig,
			this,
			this.sanitizeSystemConfigApiResponse
		);
	}

	public updateSystemConfig(
		options: {
			configuration: SystemConfig,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<SystemConfig>> {
		const {
			configuration,
			withFullResponse = true
		} = options;

		this.logger.debug( 'pre-updateTenantConfig call:', configuration );

		const systemConfigurationBodyDTO: SystemConfigurationBodyDTO = {
			type: configuration?.type as unknown as ConfigTypeDTO,
			archiving: {
				enabled: configuration.allowUpdateEnable
								 ? configuration?.archiving?.enabled
								 : configuration?.archivingConfigs?.systemConfigs?.enabled,
				ersCheck: configuration?.archiving?.ersCheck,
				strictReferenceCheck: configuration?.archiving?.strictReferenceCheck
			}
		};

		const requestParam: UpdateTenantSystemConfigRequestParams = {
			ifMatch: configuration?.archivingConfigs?.systemConfigEtag ?? '-1',
			systemConfigurationBodyDTO
		};

		const req$ = this._tenantApiClient.updateTenantSystemConfig( requestParam, withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<SystemConfigurationDTO | HttpResponse<SystemConfigurationDTO>, SystemConfig>(
			req$,
			RequestActionEnum.UPDATE,
			SystemConfig,
			this,
			this.sanitizeSystemConfigApiResponse
		);
	}


	// Notification Config
	public readNotificationConfig( withFullResponse = true ): Observable<RequestCompleteEntityState<NotificationConfig>> {
		this.logger.debug( 'pre-readNotificationConfig call: ' );
		const req$ = this._tenantApiClient.getTenantNotificationConfig( withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<NotificationConfigurationDTO | HttpResponse<NotificationConfigurationDTO>, NotificationConfig>(
			req$,
			RequestActionEnum.READ,
			NotificationConfig,
			this
		);
	}

	public createNotificationConfig( configuration: NotificationConfig, withFullResponse = true ): Observable<RequestCompleteEntityState<NotificationConfig>> {
		this.logger.debug( 'pre-createNotificationConfig call: ', configuration );
		const notificationConfigurationDTO = this._transition.prepareEntityRequest<NotificationConfig, NotificationConfigurationDTO>(
			configuration,
			ClsNotificationConfigurationDTO,
			this
		);
		const requestParam: CreateTenantNotificationConfigRequestParams = {
			notificationConfigurationDTO
		};

		const req$ = this._tenantApiClient.createTenantNotificationConfig( requestParam, withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<NotificationConfigurationDTO | HttpResponse<NotificationConfigurationDTO>, NotificationConfig>(
			req$,
			RequestActionEnum.CREATE,
			NotificationConfig,
			this
		);
	}

	public updateNotificationConfig( configuration: NotificationConfig, withFullResponse = true ): Observable<RequestCompleteEntityState<NotificationConfig>> {
		this.logger.debug( 'pre-updateNotificationConfig call: ', configuration );

		const notificationConfigurationDTO = this._transition.prepareEntityRequest<NotificationConfig, NotificationConfigurationDTO>(
			configuration,
			ClsNotificationConfigurationDTO,
			this
		);
		const requestParam: UpdateTenantNotificationConfigRequestParams = {
			ifMatch: configuration?.etag ?? '-1',
			notificationConfigurationDTO
		};

		const req$ = this._tenantApiClient.updateTenantNotificationConfig( requestParam, withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<NotificationConfigurationDTO | HttpResponse<NotificationConfigurationDTO>, NotificationConfig>(
			req$,
			RequestActionEnum.UPDATE,
			NotificationConfig,
			this
		);
	}
}
